import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

import { IDocument, normalizeDocument } from '../../Interfaces/IDocument';
import { IDocumentDirectory } from '../../Interfaces/IDocumentDirectory';
import { IDocumentDirectoryHasDocument } from '../../Interfaces/IDocumentDirectoryHasDocument';
import { IInvoiceFullObject } from '../../Interfaces/IInvoice';
import { ILegalClient } from '../../Interfaces/ILegalClient';
import { ILegalPhaseFullObject } from '../../Interfaces/ILegalPhase';
import { ILegalRole } from '../../Interfaces/ILegalRole';
import { getFetch } from '../../hooks/useFetch';
import {
  IDocumentWithScan,
  useCheckDocumentForInvoice,
} from '../../hooks/reactQuerys/InvoiceQueries';
import { scanInvoicePdf } from '../../Invoice/clientScan';
import {
  getChildDirectories,
  getDirectoryById,
  getDirectoryPath,
  getNextTempId,
} from '../DocumentDirectory/documentDirectoryUtils';
import { isSystemDirectory } from '../DocumentDirectory/documentDirectoryTypes';
import {
  DOCUMENT_AREA_LABEL,
  IDocumentLocation,
  IDocumentMoveTarget,
  isSameDocumentLocation,
} from './documentTransferTypes';

interface IDocumentTransfer {
  idLegalTransaction: number;
  /** Alle Ziele, in die ein Dokument verschoben werden kann. */
  targetArray: IDocumentMoveTarget[];
  getLocationLabel: (location: IDocumentLocation) => string;
  /**
   * Verschiebt ein Dokument; wird mit dem Vorgang gespeichert.
   *
   * Beim Ziel „Rechnungen" laeuft zuvor die QR-/Urkundennummer-Prüfung — deshalb
   * ist der Aufruf asynchron. Geprueft wird nur gelesen, geschrieben wird erst
   * beim Speichern des Vorgangs.
   */
  moveDocument: (
    documentObject: IDocument,
    from: IDocumentLocation,
    to: IDocumentLocation
  ) => Promise<void>;
  /** Laeuft gerade eine QR-Prüfung? */
  isCheckingInvoice: boolean;
  /** Benennt ein Dokument um; wird mit dem Vorgang gespeichert. */
  renameDocument: (
    documentObject: IDocument,
    location: IDocumentLocation,
    fileName: string
  ) => void;
  /**
   * Legt einen Standardordner in der internen Datenablage an und liefert dessen
   * (noch temporaere, negative) ID zurueck. Wird mit dem Vorgang gespeichert.
   */
  createDirectory: (
    title: string,
    parent_idDocumentDirectory: number | null
  ) => number;
  /** Ordner der internen Datenablage in Baumreihenfolge (mit Einrückungstiefe). */
  directoryArray: {
    idDocumentDirectory: number;
    Title: string;
    depth: number;
  }[];
}

const DocumentTransferContext = createContext<IDocumentTransfer | null>(null);

export const useDocumentTransfer = (): IDocumentTransfer | null =>
  useContext(DocumentTransferContext);

interface IProviderProps {
  idLegalTransaction: number;
  children: React.ReactNode;

  certificateArray: IDocument[];
  setCertificateArray: (documentArray: IDocument[]) => void;
  invoiceArray: IDocument[];
  setInvoiceArray: (documentArray: IDocument[]) => void;
  draftArray: IDocument[];
  setDraftArray: (documentArray: IDocument[]) => void;
  otherDocumentArray: IDocument[];
  setOtherDocumentArray: (documentArray: IDocument[]) => void;
  legalPhaseArray: ILegalPhaseFullObject[];
  setLegalPhaseArray: (legalPhaseArray: ILegalPhaseFullObject[]) => void;
  documentDirectoryArray: IDocumentDirectory[];
  setDocumentDirectoryArray: (
    documentDirectoryArray: IDocumentDirectory[]
  ) => void;
  documentArray: IDocumentDirectoryHasDocument[];
  setDocumentArray: (documentArray: IDocumentDirectoryHasDocument[]) => void;

  /** Erkannte Rechnungen (eigene Invoice-Entitaeten). */
  invoiceObjectArray: IInvoiceFullObject[];
  setInvoiceObjectArray: (invoiceObjectArray: IInvoiceFullObject[]) => void;
  /** Fuer die Zuordnung der Zahlungspflichtigen bei erkannten Rechnungen. */
  idLegalTransactionType: number;
  assignedLegalClients: ILegalClient[];
  legalRoles: ILegalRole[];
}

/**
 * Stellt das Verschieben und Umbenennen von Dokumenten fuer alle Bereiche eines
 * Vorgangs bereit (Urkunden, Rechnungen, Entwürfe, Öffentliche Dokumente,
 * Maßnahmen und die interne Datenablage).
 *
 * Alles wirkt zunaechst nur lokal: Verschieben heisst, das Dokument aus dem
 * einen Array zu entfernen und in das andere aufzunehmen, neue Ordner bekommen
 * eine temporaere negative ID. Endgueltig wird es erst beim Speichern des
 * Vorgangs — dort haengt das Backend die Zuordnungen um und ersetzt die
 * temporaeren Ordner-IDs.
 */
export const DocumentTransferProvider: React.FC<IProviderProps> = props => {
  const {
    idLegalTransaction,
    certificateArray,
    setCertificateArray,
    invoiceArray,
    setInvoiceArray,
    draftArray,
    setDraftArray,
    otherDocumentArray,
    setOtherDocumentArray,
    legalPhaseArray,
    setLegalPhaseArray,
    documentDirectoryArray,
    setDocumentDirectoryArray,
    documentArray,
    setDocumentArray,
    invoiceObjectArray,
    setInvoiceObjectArray,
    idLegalTransactionType,
    assignedLegalClients,
    legalRoles,
  } = props;

  const { checkDocumentForInvoice } = useCheckDocumentForInvoice();
  const [isCheckingInvoice, setIsCheckingInvoice] = useState(false);

  const targetArray = useMemo((): IDocumentMoveTarget[] => {
    const areaTargets: IDocumentMoveTarget[] = [
      { location: { kind: 'certificate' }, label: 'Urkunden', group: 'area' },
      { location: { kind: 'invoice' }, label: 'Rechnungen', group: 'area' },
      { location: { kind: 'draft' }, label: 'Entwürfe', group: 'area' },
      {
        location: { kind: 'other' },
        label: 'Öffentliche Dokumente',
        group: 'area',
      },
    ];

    /** Maßnahmen bleiben ihrer Phase zugeordnet und werden darunter eingerückt. */
    const requirementTargets: IDocumentMoveTarget[] = [...legalPhaseArray]
      .sort((a, b) => a.Position - b.Position)
      .flatMap(legalPhase =>
        [...(legalPhase.LegalRequirementArray ?? [])]
          .sort((a, b) => a.Position - b.Position)
          .map(legalRequirement => ({
            location: {
              kind: 'requirement' as const,
              idLegalRequirement: legalRequirement.idLegalRequirement,
            },
            label: legalRequirement.LegalRequirement,
            group: 'requirement' as const,
            subGroup: legalPhase.LegalPhase,
            depth: 1,
          }))
      );

    /**
     * Ordner in Baumreihenfolge — Unterordner folgen direkt auf ihren
     * Elternordner und werden entsprechend ihrer Tiefe eingerückt.
     */
    const directoryTargets: IDocumentMoveTarget[] = [];

    const collectDirectories = (
      parent_idDocumentDirectory: number | null,
      depth: number
    ) => {
      getChildDirectories(
        documentDirectoryArray,
        parent_idDocumentDirectory
      ).forEach(documentDirectory => {
        if (!isSystemDirectory(documentDirectory)) {
          directoryTargets.push({
            location: {
              kind: 'directory' as const,
              idDocumentDirectory: documentDirectory.idDocumentDirectory,
            },
            label: documentDirectory.Title,
            group: 'directory',
            depth: depth,
            hint:
              depth === 0
                ? undefined
                : getDirectoryPath(
                    documentDirectoryArray,
                    documentDirectory.idDocumentDirectory
                  )
                    .map(entry => entry.Title)
                    .join(' / '),
          });
        }

        collectDirectories(documentDirectory.idDocumentDirectory, depth + 1);
      });
    };

    collectDirectories(null, 0);

    /// Reihenfolge im Dialog: Vorgang, interne Datenablage, dann Abwicklung.
    return [...areaTargets, ...directoryTargets, ...requirementTargets];
  }, [legalPhaseArray, documentDirectoryArray]);

  /** Ordnerliste fuer die Auswahl eines uebergeordneten Ordners. */
  const directoryArray = useMemo(() => {
    const result: {
      idDocumentDirectory: number;
      Title: string;
      depth: number;
    }[] = [];

    const collect = (
      parent_idDocumentDirectory: number | null,
      depth: number
    ) => {
      getChildDirectories(
        documentDirectoryArray,
        parent_idDocumentDirectory
      ).forEach(documentDirectory => {
        if (!isSystemDirectory(documentDirectory)) {
          result.push({
            idDocumentDirectory: documentDirectory.idDocumentDirectory,
            Title: documentDirectory.Title,
            depth: depth,
          });
        }

        collect(documentDirectory.idDocumentDirectory, depth + 1);
      });
    };

    collect(null, 0);

    return result;
  }, [documentDirectoryArray]);

  /**
   * Neuer Ordner mit temporaerer (negativer) ID — genau wie in der Datenablage
   * selbst. Beim Speichern des Vorgangs ersetzt `syncDocumentDirectories` die
   * temporaere ID durch die echte, auch fuer die darin liegenden Dateien.
   */
  const createDirectory = useCallback(
    (title: string, parent_idDocumentDirectory: number | null): number => {
      const idDocumentDirectory = getNextTempId(
        documentDirectoryArray.map(entry => entry.idDocumentDirectory)
      );

      setDocumentDirectoryArray([
        ...documentDirectoryArray,
        {
          idDocumentDirectory: idDocumentDirectory,
          idLegalTransaction: idLegalTransaction,
          Title: title,
          Parent_idDocumentDirectory: parent_idDocumentDirectory,
          Created_at: null,
          DirectoryType: 'standard',
        },
      ]);

      return idDocumentDirectory;
    },
    [idLegalTransaction, documentDirectoryArray, setDocumentDirectoryArray]
  );

  const getLocationLabel = useCallback(
    (location: IDocumentLocation): string => {
      if (location.kind === 'requirement') {
        const legalRequirement = legalPhaseArray
          .flatMap(legalPhase => legalPhase.LegalRequirementArray ?? [])
          .find(
            entry => entry.idLegalRequirement === location.idLegalRequirement
          );

        return legalRequirement === undefined
          ? 'Maßnahme'
          : `Maßnahme: ${legalRequirement.LegalRequirement}`;
      }

      if (location.kind === 'directory') {
        const documentDirectory = getDirectoryById(
          documentDirectoryArray,
          location.idDocumentDirectory
        );

        return documentDirectory === undefined
          ? 'Interne Datenablage'
          : `Interne Datenablage / ${documentDirectory.Title}`;
      }

      return DOCUMENT_AREA_LABEL[location.kind];
    },
    [legalPhaseArray, documentDirectoryArray]
  );

  /** Dokument aus dem Array einer Fundstelle entfernen. */
  const removeFromLocation = useCallback(
    (idDocument: number, location: IDocumentLocation) => {
      const without = (array: IDocument[]) =>
        array.filter(entry => entry.idDocument !== idDocument);

      switch (location.kind) {
        case 'certificate':
          setCertificateArray(without(certificateArray));
          return;
        case 'invoice':
          setInvoiceArray(without(invoiceArray));
          return;
        case 'invoiceObject':
          setInvoiceObjectArray(
            invoiceObjectArray.filter(entry => entry.idDocument !== idDocument)
          );
          return;
        case 'draft':
          setDraftArray(without(draftArray));
          return;
        case 'other':
          setOtherDocumentArray(without(otherDocumentArray));
          return;
        case 'directory':
          setDocumentArray(
            documentArray.filter(entry => entry.idDocument !== idDocument)
          );
          return;
        case 'requirement':
          setLegalPhaseArray(
            legalPhaseArray.map(legalPhase => ({
              ...legalPhase,
              LegalRequirementArray: (
                legalPhase.LegalRequirementArray ?? []
              ).map(legalRequirement =>
                legalRequirement.idLegalRequirement ===
                location.idLegalRequirement
                  ? {
                      ...legalRequirement,
                      DocumentArrayEmployees: (
                        legalRequirement.DocumentArrayEmployees ?? []
                      ).filter(entry => entry.idDocument !== idDocument),
                      DocumentArrayClient: (
                        legalRequirement.DocumentArrayClient ?? []
                      ).filter(entry => entry.idDocument !== idDocument),
                    }
                  : legalRequirement
              ),
            }))
          );
          return;
      }
    },
    [
      certificateArray,
      setCertificateArray,
      invoiceArray,
      setInvoiceArray,
      draftArray,
      setDraftArray,
      otherDocumentArray,
      setOtherDocumentArray,
      documentArray,
      setDocumentArray,
      invoiceObjectArray,
      setInvoiceObjectArray,
      legalPhaseArray,
      setLegalPhaseArray,
    ]
  );

  /** Dokument in das Array der Zielfundstelle aufnehmen. */
  const addToLocation = useCallback(
    (documentObject: IDocument, location: IDocumentLocation) => {
      const withEntry = (array: IDocument[]) => [
        ...array.filter(
          entry => entry.idDocument !== documentObject.idDocument
        ),
        documentObject,
      ];

      switch (location.kind) {
        case 'certificate':
          setCertificateArray(withEntry(certificateArray));
          return;
        case 'invoice':
          setInvoiceArray(withEntry(invoiceArray));
          return;
        case 'draft':
          setDraftArray(withEntry(draftArray));
          return;
        case 'other':
          setOtherDocumentArray(withEntry(otherDocumentArray));
          return;
        case 'directory':
          setDocumentArray([
            ...documentArray.filter(
              entry => entry.idDocument !== documentObject.idDocument
            ),
            {
              ...documentObject,
              idDocumentDirectory: location.idDocumentDirectory,
              idLegalTransaction: idLegalTransaction,
            } as IDocumentDirectoryHasDocument,
          ]);
          return;
        case 'requirement':
          setLegalPhaseArray(
            legalPhaseArray.map(legalPhase => ({
              ...legalPhase,
              LegalRequirementArray: (
                legalPhase.LegalRequirementArray ?? []
              ).map(legalRequirement =>
                legalRequirement.idLegalRequirement ===
                location.idLegalRequirement
                  ? {
                      ...legalRequirement,
                      DocumentArrayEmployees: withEntry(
                        legalRequirement.DocumentArrayEmployees ?? []
                      ),
                    }
                  : legalRequirement
              ),
            }))
          );
          return;
      }
    },
    [
      idLegalTransaction,
      certificateArray,
      setCertificateArray,
      invoiceArray,
      setInvoiceArray,
      draftArray,
      setDraftArray,
      otherDocumentArray,
      setOtherDocumentArray,
      documentArray,
      setDocumentArray,
      legalPhaseArray,
      setLegalPhaseArray,
    ]
  );

  /** Dateiinhalt nachladen — die QR-Prüfung braucht das PDF. */
  const loadDocumentData = useCallback(
    (documentObject: IDocument): Promise<IDocument> =>
      new Promise(resolve => {
        if (
          documentObject.Data !== undefined ||
          documentObject.idDocument < 0
        ) {
          resolve(documentObject);
          return;
        }

        getFetch(
          '/document/',
          documentObject.idDocument,
          (loaded: IDocument) =>
            resolve(
              loaded === null || loaded === undefined
                ? documentObject
                : normalizeDocument(loaded)
            ),
          (isSuccessful: boolean) => {
            if (!isSuccessful) {
              resolve(documentObject);
            }
          }
        );
      }),
    []
  );

  /**
   * Zahlungspflichtige einer neu erkannten Rechnung — dieselbe Regel wie beim
   * Upload im Rechnungsreiter: bei genau einem Mandanten dieser, sonst alle
   * Mandanten mit einer zahlungspflichtigen Rolle.
   */
  const getPayerLegalClientIdArray = useCallback((): number[] => {
    if (assignedLegalClients.length === 1) {
      return [assignedLegalClients[0].idLegalClient];
    }

    return legalRoles
      .filter(legalRole => legalRole.isPayer)
      .flatMap(legalRole =>
        assignedLegalClients
          .filter(
            legalClient => legalClient.idLegalRole === legalRole.idLegalRole
          )
          .map(legalClient => legalClient.idLegalClient)
      );
  }, [assignedLegalClients, legalRoles]);

  /**
   * Verschieben nach „Rechnungen": QR-Code und Urkundennummer werden geprueft.
   * Wird ein Betrag erkannt, entsteht daraus eine erkannte Rechnung, sonst
   * landet die Datei in der Liste der nicht erkannten Rechnungen.
   */
  const moveToInvoice = useCallback(
    async (documentObject: IDocument, from: IDocumentLocation) => {
      setIsCheckingInvoice(true);

      try {
        const loadedDocument = await loadDocumentData(documentObject);
        const documentWithScan: IDocumentWithScan = { ...loadedDocument };

        if (loadedDocument.idMIMEType === 1 && loadedDocument.Data) {
          try {
            const scanResult = await scanInvoicePdf(
              Uint8Array.from(atob(String(loadedDocument.Data)), character =>
                character.charCodeAt(0)
              ).buffer
            );

            if (
              scanResult.qrPayload ||
              scanResult.combined.InvoiceNumber ||
              scanResult.combined.Total ||
              scanResult.combined.IBAN
            ) {
              documentWithScan.ClientScan = {
                EPCString: scanResult.combined.EPCString,
                Total: scanResult.combined.Total,
                InvoiceNumber: scanResult.combined.InvoiceNumber,
                IBAN: scanResult.combined.IBAN || undefined,
                BIC: scanResult.combined.BIC || undefined,
                Recipient: scanResult.combined.Recipient || undefined,
              };
            }
          } catch (error) {
            console.warn('[Invoice] Client-Scan fehlgeschlagen:', error);
          }
        }

        const checkedArray = await checkDocumentForInvoice([documentWithScan]);
        const checkedInvoice = checkedArray?.[0];

        removeFromLocation(documentObject.idDocument, from);

        if (checkedInvoice === undefined || checkedInvoice.Total === null) {
          addToLocation(loadedDocument, { kind: 'invoice' });
          return;
        }

        // Erkannte Rechnung: eigene Invoice-Entitaet mit temporaerer ID, das
        // bestehende Dokument wird dabei wiederverwendet.
        setInvoiceObjectArray([
          ...invoiceObjectArray.filter(
            entry => entry.idDocument !== documentObject.idDocument
          ),
          {
            ...checkedInvoice,
            idInvoice: getNextTempId(
              invoiceObjectArray.map(entry => entry.idInvoice)
            ),
            idDocument: documentObject.idDocument,
            idLegalTransaction: idLegalTransaction,
            idLegalTransactionType: idLegalTransactionType,
            LegalClients: getPayerLegalClientIdArray(),
          },
        ]);
      } finally {
        setIsCheckingInvoice(false);
      }
    },
    [
      loadDocumentData,
      checkDocumentForInvoice,
      removeFromLocation,
      addToLocation,
      invoiceObjectArray,
      setInvoiceObjectArray,
      idLegalTransaction,
      idLegalTransactionType,
      getPayerLegalClientIdArray,
    ]
  );

  const moveDocument = useCallback(
    async (
      documentObject: IDocument,
      from: IDocumentLocation,
      to: IDocumentLocation
    ) => {
      if (isSameDocumentLocation(from, to)) {
        return;
      }

      if (to.kind === 'invoice') {
        await moveToInvoice(documentObject, from);
        return;
      }

      removeFromLocation(documentObject.idDocument, from);
      addToLocation(documentObject, to);
    },
    [removeFromLocation, addToLocation, moveToInvoice]
  );

  const renameDocument = useCallback(
    (
      documentObject: IDocument,
      location: IDocumentLocation,
      fileName: string
    ) => {
      const rename = (array: IDocument[]) =>
        array.map(entry =>
          entry.idDocument === documentObject.idDocument
            ? { ...entry, FileName: fileName }
            : entry
        );

      switch (location.kind) {
        case 'certificate':
          setCertificateArray(rename(certificateArray));
          return;
        case 'invoice':
          setInvoiceArray(rename(invoiceArray));
          return;
        case 'invoiceObject':
          setInvoiceObjectArray(
            invoiceObjectArray.map(entry =>
              entry.idDocument === documentObject.idDocument
                ? { ...entry, FileName: fileName }
                : entry
            )
          );
          return;
        case 'draft':
          setDraftArray(rename(draftArray));
          return;
        case 'other':
          setOtherDocumentArray(rename(otherDocumentArray));
          return;
        case 'directory':
          setDocumentArray(
            documentArray.map(entry =>
              entry.idDocument === documentObject.idDocument
                ? { ...entry, FileName: fileName }
                : entry
            )
          );
          return;
        case 'requirement':
          setLegalPhaseArray(
            legalPhaseArray.map(legalPhase => ({
              ...legalPhase,
              LegalRequirementArray: (
                legalPhase.LegalRequirementArray ?? []
              ).map(legalRequirement =>
                legalRequirement.idLegalRequirement ===
                location.idLegalRequirement
                  ? {
                      ...legalRequirement,
                      DocumentArrayEmployees: rename(
                        legalRequirement.DocumentArrayEmployees ?? []
                      ),
                      DocumentArrayClient: rename(
                        legalRequirement.DocumentArrayClient ?? []
                      ),
                    }
                  : legalRequirement
              ),
            }))
          );
          return;
      }
    },
    [
      certificateArray,
      setCertificateArray,
      invoiceArray,
      setInvoiceArray,
      draftArray,
      setDraftArray,
      otherDocumentArray,
      setOtherDocumentArray,
      documentArray,
      setDocumentArray,
      invoiceObjectArray,
      setInvoiceObjectArray,
      legalPhaseArray,
      setLegalPhaseArray,
    ]
  );

  const value = useMemo(
    (): IDocumentTransfer => ({
      idLegalTransaction: idLegalTransaction,
      targetArray: targetArray,
      getLocationLabel: getLocationLabel,
      moveDocument: moveDocument,
      isCheckingInvoice: isCheckingInvoice,
      renameDocument: renameDocument,
      createDirectory: createDirectory,
      directoryArray: directoryArray,
    }),
    [
      idLegalTransaction,
      targetArray,
      getLocationLabel,
      moveDocument,
      isCheckingInvoice,
      renameDocument,
      createDirectory,
      directoryArray,
    ]
  );

  return (
    <DocumentTransferContext.Provider value={value}>
      {props.children}
    </DocumentTransferContext.Provider>
  );
};
